import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { GroupNameInput } from './GraphGroupsBar';

/**
 * Which groups a graph (or the ticked graphs) is in — a small popover with a
 * checkbox per group and "+ Neue Gruppe" typed in place.
 *   checked(group)  true | false | 'some' (only some of the ticked graphs)
 *   onPick(group)   tick / untick (one graph: stays open) or add (closeOnPick)
 *   onCreate(name)  Promise — a new group with the graph(s) in it
 */
export function GroupPickerPopover({ anchorEl, onClose, title, groups, checked, onPick, onCreate, closeOnPick = false }) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  useEffect(() => { if (!anchorEl) setCreating(false); }, [anchorEl]);

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { mt: 0.5, minWidth: 240, maxWidth: 320, borderRadius: 2, py: 1 } }}
    >
      <Typography variant="caption" sx={{ px: 2, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
        {title}
      </Typography>
      <Box role="group" aria-label={title} sx={{ maxHeight: 280, overflowY: 'auto' }}>
        {groups.map(group => {
          const state = checked(group);
          return (
            <Box
              component="label"
              key={group.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 2, py: 0.25, cursor: 'pointer',
                fontSize: 14, color: 'text.primary', '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Checkbox
                size="small"
                checked={state === true} indeterminate={state === 'some'}
                onChange={() => { onPick(group); if (closeOnPick) onClose(); }}
                inputProps={{ 'aria-label': group.name }}
                sx={{ p: 0.75 }}
              />
              <Box component="span" sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {group.name}
              </Box>
              <Box component="span" sx={{ fontSize: 12, color: 'text.disabled' }}>{group.count}</Box>
            </Box>
          );
        })}
      </Box>
      {groups.length > 0 && <Divider sx={{ my: 0.75 }} />}
      <Box sx={{ px: 1.5, pt: groups.length ? 0 : 0.5 }}>
        {creating ? (
          <GroupNameInput
            sx={{ width: '100%' }}
            onSubmit={async (name) => { await onCreate(name); setCreating(false); if (closeOnPick) onClose(); }}
            onCancel={() => setCreating(false)} />
        ) : (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            {t('graphs_group_new', { defaultValue: 'Neue Gruppe' })}
          </Button>
        )}
      </Box>
    </Popover>
  );
}

/**
 * Inside a group: pick saved graphs that are not in it yet; they go to the
 * end of the group in the order they were ticked.
 */
export function AddGraphsDialog({ open, group, graphs, onClose, onAdd, dateText }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState([]);
  useEffect(() => { if (open) setPicked([]); }, [open]);
  const candidates = group ? graphs.filter(g => !group.graph_ids.includes(g.id)) : [];
  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
        {t('graphs_group_add_title', { defaultValue: 'Grafiken zu „{{name}}“ hinzufügen', name: group?.name || '' })}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1, maxHeight: 420 }}>
        {candidates.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', p: 1 }}>
            {t('graphs_group_add_none', { defaultValue: 'Alle Grafiken sind schon in dieser Gruppe.' })}
          </Typography>
        ) : candidates.map(graph => (
          <Box
            component="label"
            key={graph.id}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, borderRadius: 1, p: 0.5, cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Checkbox size="small" checked={picked.includes(graph.id)} onChange={() => toggle(graph.id)}
              inputProps={{ 'aria-label': graph.title }} sx={{ p: 0.75 }} />
            <Box sx={{ minWidth: 0, pt: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{graph.title}</Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>{dateText(graph)}</Typography>
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
        <Button variant="contained" disabled={!picked.length} onClick={() => onAdd(picked)}>
          {t('graphs_group_add_confirm', { defaultValue: 'Hinzufügen' })}{picked.length ? ` (${picked.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
